import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Modal } from '../index';
import { CalendarEvent } from '../../types/api';

export interface RescheduleData {
  newDate: Date;
  newStartTime: string;
  newEndTime: string;
  applyToFuture: boolean;
}

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: RescheduleData) => void;
  onDelete?: () => void;
  event: CalendarEvent | null;
  newDate: Date | null;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onDelete,
  event,
  newDate,
}) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [applyToFuture, setApplyToFuture] = useState(false);

  // 모달이 열릴 때 기존 시간으로 초기화
  useEffect(() => {
    if (event && isOpen) {
      const originalStart = new Date(event.start);
      const originalEnd = new Date(event.end);
      setStartTime(format(originalStart, 'HH:mm'));
      setEndTime(format(originalEnd, 'HH:mm'));
      setApplyToFuture(false);
    }
  }, [event, isOpen]);

  if (!event || !newDate) return null;

  const originalDate = new Date(event.start);
  const eventData = event.data as Record<string, any>;
  const scheduleType = eventData?.scheduleType || '';
  const studentName = eventData?.studentName || event.title;
  const isRecurring = scheduleType === 'regular' || eventData?.isRecurring;

  const getScheduleTypeLabel = (type: string) => {
    switch (type) {
      case 'regular':
        return '정규 수업';
      case 'rescheduled':
        return '변경된 수업';
      case 'personal':
        return '개인 일정';
      default:
        return '일정';
    }
  };

  const handleConfirm = () => {
    onConfirm({
      newDate,
      newStartTime: startTime,
      newEndTime: endTime,
      applyToFuture,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="일정 변경"
      size="sm"
    >
      <div className="reschedule-modal-content">
        <div className="reschedule-info">
          <div className="schedule-type-badge" style={{ backgroundColor: event.color }}>
            {getScheduleTypeLabel(scheduleType)}
          </div>
          <h3 className="schedule-title">{studentName}</h3>
        </div>

        <div className="reschedule-dates">
          <div className="date-row">
            <span className="date-label">변경 전</span>
            <span className="date-value original">
              {format(originalDate, 'M월 d일 (E)', { locale: ko })}
              <span className="time">
                {format(originalDate, 'HH:mm')} - {format(new Date(event.end), 'HH:mm')}
              </span>
            </span>
          </div>
          <div className="date-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </div>
          <div className="date-row">
            <span className="date-label">변경 후</span>
            <span className="date-value new">
              {format(newDate, 'M월 d일 (E)', { locale: ko })}
            </span>
          </div>
        </div>

        {/* 시간 변경 */}
        <div className="time-change-section">
          <label className="section-label">변경할 시간</label>
          <div className="time-inputs">
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="time-input"
            />
            <span className="time-separator">~</span>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="time-input"
            />
          </div>
        </div>

        {/* 반복 일정인 경우 이후 일정 적용 옵션 */}
        {isRecurring && (
          <div className="future-apply-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={applyToFuture}
                onChange={(e) => setApplyToFuture(e.target.checked)}
              />
              <span className="checkbox-text">
                이 이후의 일정을 모두 동일하게 변경
              </span>
            </label>
            {!applyToFuture && (
              <p className="apply-notice">
                체크하지 않으면 이번 주만 임시 변경됩니다.
              </p>
            )}
            {applyToFuture && (
              <p className="apply-notice warning">
                이후 모든 정규 수업 일정이 변경됩니다.
              </p>
            )}
          </div>
        )}

        <div className="modal-buttons">
          <button className="btn btn-secondary" onClick={onClose}>
            취소
          </button>
          {onDelete && (scheduleType === 'personal' || scheduleType === 'meeting' || scheduleType === 'break') && (
            <button className="btn btn-danger" onClick={onDelete}>
              삭제
            </button>
          )}
          <button className="btn btn-primary" onClick={handleConfirm}>
            변경하기
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default RescheduleModal;
